const nearZero = (n) => Math.abs(Number(n) || 0) < 0.01;

/** Fila Excel que representa complemento de pago (PPD / parcial / modo complemento). */
export function excelRegistroEsComplementoPago(row) {
  if (!row) return false;
  if ((row.registro_confronta || "") === "complemento_pago") return true;
  const mp = String(row.metodo_pago || "").toUpperCase();
  if (mp.includes("PPD")) return true;
  const pct = Number(row.pct_cobro ?? row.parcialidades_excel ?? 100);
  if (pct > 0.5 && pct < 99.5) return true;
  if (String(row.excel_source || "") === "modo_complemento") {
    return (row.modo_complemento_tipo || "") === "parcial" || pct < 99.5;
  }
  return false;
}

/** Totales Excel para columnas de contabilidad: cruce UUID, contabilidad del periodo o respaldo. */
export function pickExcelDisplayTotales(totales, { nomina = false, excelMeta = null } = {}) {
  const cruceKey = nomina ? "neto" : "total";
  const contKey = nomina ? "neto" : "total";
  const cruce = totales?.excel || {};
  const cont = totales?.excel_contabilidad;
  const display = totales?.excel_display;
  const respaldo = excelMeta?.montos_periodo_respaldo;
  const metaCont = excelMeta?.montos_contabilidad;
  const pick = (obj) => {
    if (!obj) return null;
    const v = obj[contKey] ?? obj.total ?? obj.neto;
    return nearZero(v) ? null : obj;
  };
  return (
    pick(display) ||
    pick(cont) ||
    pick(metaCont) ||
    pick(respaldo) ||
    display ||
    cont ||
    respaldo ||
    metaCont ||
    cruce
  );
}

/** IVA SAT bruto (PUE + Pago) sin ajuste de display. */
export function sistemaIvaBruto(totales) {
  const pue = totales?.sistema?.pue || {};
  const pago = totales?.sistema?.pago || {};
  return roundMoney((pue.iva || 0) + (pago.iva || 0));
}

/** IVA de complementos P que aún inflan SAT pero no contabilidad (hasta cancelar/ajustar). */
export function confrontaComplementoPIvaPendiente(totales, excelDisplayTotales = null) {
  const explicit = totales?.complemento_p_iva_pendiente;
  if (explicit != null && Number.isFinite(Number(explicit)) && Number(explicit) > 0.01) {
    return roundMoney(explicit);
  }
  const display = resolveSistemaDisplayParaResumen(totales, excelDisplayTotales);
  const excl = display?.pago_excluido_resumen;
  if (excl && Number(excl.iva) > 0.01) {
    return roundMoney(excl.iva);
  }
  const gap = roundMoney(
    sistemaIvaBruto(totales) - sistemaIvaDesdeDesglose(totales, excelDisplayTotales),
  );
  return gap > 0.01 ? gap : 0;
}

/** Display SAT efectivo: API o inferencia junio (PUE = contabilidad + complemento P en bruto). */
export function resolveSistemaDisplayParaResumen(totales, excelDisplayTotales = null) {
  const ex = excelDisplayTotales || pickExcelDisplayTotales(totales);
  const apiDisplay = totales?.sistema_display;
  if (apiDisplay?.pago_excluido_resumen) {
    return apiDisplay;
  }
  if (apiDisplay && Number(apiDisplay.iva) > 0.01) {
    return apiDisplay;
  }
  const pue = totales?.sistema?.pue || {};
  const pago = totales?.sistema?.pago || {};
  const pueIva = Number(pue.iva) || 0;
  const pagoIva = Number(pago.iva) || 0;
  const exIva = Number(ex?.iva) || 0;
  if (pagoIva > 0.01 && exIva > 0.01 && Math.abs(pueIva - exIva) < 0.01) {
    const traza = (totales?.complementos_p_trazabilidad || []).filter(
      (c) => Number(c?.iva) > 0.01,
    );
    return {
      ...(apiDisplay || {}),
      iva: exIva,
      subtotal: Number(ex.subtotal) || roundMoney(Number(pue.subtotal) || 0),
      total: Number(ex.total) || roundMoney(Number(pue.total) || 0),
      pue: { ...pue },
      pago: { subtotal: 0, iva: 0, total: 0, count: 0 },
      pago_excluido_resumen: {
        subtotal: roundMoney(Number(pago.subtotal) || 0),
        iva: roundMoney(pagoIva),
        total: roundMoney(Number(pago.total) || 0),
        count: Number(pago.count) || 0,
        motivo: "complemento_p_no_contable_inferido",
        complementos: traza.length ? traza : undefined,
      },
    };
  }
  return apiDisplay || null;
}

/** IVA SAT de visualización = IVA PUE + IVA complementos de pago (neto, sin cancelados). */
export function sistemaIvaDesdeDesglose(totales, excelDisplayTotales = null) {
  const display = resolveSistemaDisplayParaResumen(totales, excelDisplayTotales);
  if (display && Number.isFinite(Number(display.iva))) {
    return roundMoney(display.iva);
  }
  const pue = totales?.sistema?.pue || {};
  const pago = totales?.sistema?.pago || {};
  return roundMoney((pue.iva || 0) + (pago.iva || 0));
}

/** Desglose PUE/Pago para columnas del resumen (usa sistema_display si el backend lo envía). */
export function sistemaDesgloseParaResumen(totales, excelDisplayTotales = null) {
  const display = resolveSistemaDisplayParaResumen(totales, excelDisplayTotales);
  const pue = display?.pue ?? totales?.sistema?.pue ?? {};
  const pago = display ? (display.pago ?? { subtotal: 0, iva: 0, total: 0, count: 0 }) : (totales?.sistema?.pago ?? {});
  const pueSub = pue.subtotal || 0;
  const pueIva = pue.iva || 0;
  const pagoSub = pago.subtotal || 0;
  const pagoIva = pago.iva || 0;
  const pueTot = pue.total || 0;
  const pagoTot = pago.total || 0;
  return {
    pueSub,
    pueIva,
    pagoSub,
    pagoIva,
    iva: sistemaIvaDesdeDesglose(totales, excelDisplayTotales),
    subtotal: Math.round((pueSub + pagoSub) * 100) / 100,
    total: Math.round((pueTot + pagoTot) * 100) / 100,
  };
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Δ IVA entre columnas del resumen (IVA SAT visible − Excel contabilidad). */
export function confrontaDiffIvaResumen(totales, excelDisplayTotales = null) {
  const ex = excelDisplayTotales || pickExcelDisplayTotales(totales);
  const ivaExcel = Number(ex?.iva) || 0;
  const ivaVisible = sistemaIvaDesdeDesglose(totales, ex);
  const visual = roundMoney(ivaVisible - ivaExcel);
  if (Math.abs(visual) > 0.01) return visual;
  const apiDisplayIva = totales?.sistema_display?.iva;
  const apiDisplayAutoritativo =
    totales?.sistema_display != null &&
    apiDisplayIva != null &&
    Number.isFinite(Number(apiDisplayIva));
  const trazaExcluida = (totales?.complementos_p_trazabilidad || []).some(
    (item) =>
      Number(item?.iva) > 0.01 &&
      (item?.cancelado === true || item?.contribuye_iva_sat === false),
  );
  if (
    apiDisplayAutoritativo &&
    Math.abs(roundMoney(Number(apiDisplayIva) - ivaExcel)) <= 0.01 &&
    trazaExcluida
  ) {
    return visual;
  }
  const bruto = roundMoney(sistemaIvaBruto(totales) - ivaExcel);
  if (Math.abs(bruto) > 0.01) return bruto;
  const ivaBrutoApi = totales?.diff_contabilidad_vs_sistema?.iva_bruto;
  if (ivaBrutoApi != null && Number.isFinite(Number(ivaBrutoApi)) && Math.abs(Number(ivaBrutoApi)) > 0.01) {
    return roundMoney(ivaBrutoApi);
  }
  const pendiente = confrontaComplementoPIvaPendiente(totales, ex);
  if (pendiente > 0.01) {
    return roundMoney(sistemaIvaBruto(totales) - ivaExcel);
  }
  const fromApi = totales?.diff_contabilidad_vs_sistema?.iva;
  if (fromApi != null && Number.isFinite(Number(fromApi))) {
    return roundMoney(fromApi);
  }
  return visual;
}

/** Δ IVA SAT − Excel contabilidad (siempre coherente con las columnas del resumen). */
export function diffIvaContabilidadVsSistema(totales, excelDisplayTotales = null) {
  return confrontaDiffIvaResumen(totales, excelDisplayTotales);
}

/** ¿El resultado de confronta de un mes/periodo tiene diferencias accionables? */
export function confrontaHasDifferences(data) {
  if (!data) return false;
  const ex = pickExcelDisplayTotales(data.totales, { excelMeta: data?.excel_meta });
  if (Math.abs(confrontaDiffIvaResumen(data.totales, ex)) > 0.01) return true;
  if (confrontaComplementoPIvaPendiente(data.totales, ex) > 0.01) return true;
  if (data.summary?.has_differences === true) return true;
  if (data.summary?.has_differences === false) return false;
  const s = data.summary || {};
  if ((s.mismatched || 0) + (s.solo_en_excel || 0) + (s.solo_en_sistema || 0) > 0) {
    return true;
  }
  const d = data.totales?.diff || {};
  const perc = d.percepciones ?? d.subtotal;
  const isr = d.isr;
  const neto = d.neto ?? d.total;
  return (
    Math.abs(perc || 0) > 0.01 ||
    Math.abs(isr || 0) > 0.01 ||
    Math.abs(neto || 0) > 0.01
  );
}

/** Filas mensuales con diferencias (utilidad opcional). */
export function filterMonthlyWithDifferences(monthly) {
  return (monthly || []).filter((blk) => confrontaHasDifferences(blk.data));
}

/** Muestra motor de confronta y schema BD (validar que no hay caché de API vieja). */
export function ConfrontaEngineBadge({ data }) {
  const engine = data?.excel_meta?.confronta_excel_engine;
  const schema = data?.excel_meta?.db_schema;
  if (!engine) return null;
  return (
    <p
      className="text-[10px] text-slate-500 font-mono"
      data-testid="confronta-excel-engine-badge"
    >
      Motor Excel: <strong>{engine}</strong>
      {schema != null ? <> · BD schema v{schema}</> : null}
      {" "}
      — Si no coincide con el despliegue, reinicie el backend y vuelva a ejecutar la confronta.
    </p>
  );
}

/** Misma lógica que backend `_confronta_row_mismo_mes_dia_distinto`. */
export function rowMismoMesDiaDistinto(row) {
  if (!row) return false;
  if (row.mismo_mes_dia_distinto) return true;
  const fs = (row.fecha_sistema || row.fecha_pago || "").slice(0, 10);
  const fe = (row.fecha_excel || row.fecha_ingreso || "").slice(0, 10);
  if (!fs || !fe || fs === fe) return false;
  return fs.slice(0, 7) === fe.slice(0, 7);
}

/** CFDI emparejados mismo mes calendario pero con día distinto SAT vs contabilidad. */
export function confrontaMismoMesDiaDistintoCount(data) {
  const n = data?.summary?.mismo_mes_dia_distinto;
  if (typeof n === "number" && n >= 0) return n;
  const matched = data?.matched || [];
  const mismatched = data?.mismatched || [];
  return [...matched, ...mismatched].filter(rowMismoMesDiaDistinto).length;
}

/** Aviso al expandir un mes cuyo cruce no tiene hallazgos. */
export function MesSinDiferenciasAviso({
  label,
  from,
  to,
  data,
  mismoMesCount,
  onDownloadMismoMes,
}) {
  const count =
    typeof mismoMesCount === "number"
      ? mismoMesCount
      : data
        ? confrontaMismoMesDiaDistintoCount(data)
        : 0;
  const showMismoMes = count > 0;

  return (
    <div className="space-y-3" data-testid="confronta-mes-sin-diferencias-wrap">
      <div
        className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950 space-y-2"
        data-testid="confronta-mes-sin-diferencias"
      >
        <p className="m-0">
          <b>Mes sin diferencias.</b>{" "}
          {label ? (
            <>
              En <strong>{label}</strong>
              {from && to ? <> ({from} → {to})</> : null},{" "}
            </>
          ) : null}
          el sistema (SAT) y la contabilidad (Excel, CSV o conciliación manual) coinciden en montos. No hay
          recibos que corregir en este mes.
          {showMismoMes ? (
            <>
              {" "}
              Hay <strong>{count}</strong>{" "}
              {count === 1 ? "CFDI" : "CFDIs"} emitido{count === 1 ? "" : "s"} y cobrado
              {count === 1 ? "" : "s"}/pagado{count === 1 ? "" : "s"} dentro del mismo mes con{" "}
              <strong>fecha distinta</strong> entre SAT y contabilidad.
            </>
          ) : null}
        </p>
        {showMismoMes && onDownloadMismoMes ? (
          <p
            className="m-0 text-xs text-emerald-900 flex flex-wrap items-center gap-x-2 gap-y-1"
            data-testid="confronta-mismo-mes-dia-distinto-aviso"
          >
            <span>Revise fechas de cobro/pago en contabilidad si lo desea; los importes ya cuadran.</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-sm border border-emerald-400 bg-white px-2.5 py-1 text-[11px] font-semibold text-emerald-950 hover:bg-emerald-100 shrink-0"
              onClick={onDownloadMismoMes}
              data-testid="dl-mismo-mes-fechas-inline"
            >
              Descargar Excel ({count})
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Aviso: total contable del Excel ≠ filas cruzadas por UUID (sin CFDI en detalle). */
export function ConfrontaHuecoCruceBanner({ data }) {
  const s = data?.summary || {};
  const hueco = data?.totales?.excel_fuera_cruce_uuid;
  const cruce = data?.totales?.excel;
  const ht = Math.abs(Number(hueco?.total ?? 0));
  if (ht < 0.01) return null;
  const rowDiffs =
    (s.mismatched || 0) + (s.solo_en_excel || 0) + (s.solo_en_sistema || 0);
  if (rowDiffs > 0) return null;
  return (
    <div
      className="rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-950"
      data-testid="confronta-hueco-cruce-banner"
    >
      <b>El Δ Total del resumen ya no incluye filas fuera del cruce.</b>{" "}
      Hay {fmtContTotal(hueco)} en Excel (contabilidad) que no tienen UUID SAT válido en este mes;
      el cruce por CFDI suma {fmtContTotal(cruce)}. Revise la pestaña{" "}
      <strong>Coincidentes</strong> o cargue Folio Fiscal en esas filas en Carga de información.
    </div>
  );
}

/** Aviso cuando el SAT tiene datos pero Excel del flujo no (ingresos, egresos o nóminas). */
export function ExcelConfrontaDiagnostico({ data }) {
  const diag = data?.excel_meta?.diagnostico;
  const cont = data?.excel_meta?.montos_contabilidad;
  const cruce = data?.totales?.excel;
  const flujoFiltrado = diag?.codigo === "EXCEL_FLUJO_FILTRADO";
  const showContHint =
    cont &&
    cruce &&
    (nearZero(cruce.total ?? cruce.neto) && !nearZero(cont.total ?? cont.neto));
  const showContWithFlujo =
    flujoFiltrado &&
    cont &&
    !nearZero(cont.total ?? cont.neto);
  if (!diag?.mensaje && !showContHint && !showContWithFlujo) return null;
  if (showContWithFlujo) {
    return (
      <div
        className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950"
        data-testid="confronta-excel-diagnostico-contabilidad-con-flujo"
      >
        <b>Contabilidad cargada ({fmtContTotal(cont)}).</b> Hay filas en el Excel del período pero el
        cruce por UUID con el SAT quedó vacío (revisar Folio Fiscal / clasificación ingreso). Los
        totales verdes usan contabilidad del período.{" "}
        <a href="/carga-informacion" className="underline font-semibold">
          Carga de información
        </a>
      </div>
    );
  }
  if (!diag?.mensaje && showContHint) {
    return (
      <div
        className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-950"
        data-testid="confronta-excel-diagnostico-contabilidad-cargada"
      >
        <b>Contabilidad cargada.</b> Los importes de Excel (Contabilidad) se muestran en las columnas
        verdes; el cruce por UUID con el SAT sigue en cero hasta que el Folio Fiscal coincida con los CFDI.{" "}
        <a href="/carga-informacion" className="underline font-semibold">
          Carga de información
        </a>
      </div>
    );
  }
  const isVacio = diag.codigo === "EXCEL_VACIO";
  const sinUuid = diag.codigo === "EXCEL_SIN_UUID_VALIDO";
  const fechasCobro = diag.codigo === "EXCEL_FECHAS_COBRO_UN_MES";
  const title = isVacio
    ? "Sin contabilidad en este flujo."
    : fechasCobro
      ? "Fechas de cobro concentradas en un mes."
      : flujoFiltrado
        ? "Excel en el período, pero no como ingreso/egreso."
        : sinUuid
          ? "Contabilidad sin UUID reconocible."
          : "Excel sin montos útiles.";
  return (
    <div
      className={`rounded-sm border px-4 py-3 text-xs ${
        isVacio
          ? "border-red-200 bg-red-50 text-red-950"
          : fechasCobro
            ? "border-orange-200 bg-orange-50 text-orange-950"
            : flujoFiltrado
              ? "border-violet-200 bg-violet-50 text-violet-950"
              : sinUuid
                ? "border-sky-200 bg-sky-50 text-sky-950"
                : "border-amber-200 bg-amber-50 text-amber-950"
      }`}
      data-testid={`confronta-excel-diagnostico-${diag.codigo || "aviso"}`}
    >
      <b>{title}</b> {diag.mensaje}{" "}
      <a href="/carga-informacion" className="underline font-semibold">
        Carga de información
      </a>
    </div>
  );
}
